<script setup>
import {ref, defineProps, defineEmits, defineExpose, defineModel, onMounted, onBeforeUnmount} from 'vue'

defineProps({
  form_id: {
    type: String,
    required: false,
  },
})

const isValid = defineModel('valid')

const emit = defineEmits(['on-submitted', 'on-validation-failed', 'was-validated'])

const was_validated = ref(false)
const form = ref(null)

let validityObserver = null

function submitForm() {
  return emit(isValidForm() ? 'on-submitted' : 'on-validation-failed')
}

function isValidForm() {
  emit('was-validated', was_validated.value)
  was_validated.value = true

  if (!form.value.checkValidity()) {
    return false
  }

  was_validated.value = false
  return true
}

function reset() {
  was_validated.value = false
  form.value?.querySelectorAll('.form-row-component.was-validated').forEach(row => {
    row.classList.remove('was-validated')
  })
}

function updateValidity() {
  isValid.value = form.value.checkValidity()
}

function markRowAsValidated(event) {
  event.target.closest('.form-row-component')?.classList.add('was-validated')
}

function startValidityTracking() {
  form.value.addEventListener('input', updateValidity)
  form.value.addEventListener('change', updateValidity)
  form.value.addEventListener('focusout', markRowAsValidated)

  validityObserver = new MutationObserver(updateValidity)
  validityObserver.observe(form.value, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['required', 'disabled', 'readonly'],
  })

  updateValidity()
}

function stopValidityTracking() {
  form.value?.removeEventListener('input', updateValidity)
  form.value?.removeEventListener('change', updateValidity)
  form.value?.removeEventListener('focusout', markRowAsValidated)
  validityObserver?.disconnect()
}

onMounted(() => {
  if (isValid.value === undefined) {
    return
  }

  startValidityTracking()
})

onBeforeUnmount(() => {
  stopValidityTracking()
})

defineExpose({reset})
</script>

<template>
  <form
    @submit.prevent="submitForm"
    :class="['needs-validation form m-form', was_validated ? 'was-validated' : '']"
    :id="form_id"
    ref="form"
    novalidate="true"
  >
    <slot></slot>
  </form>
</template>
